<template>
  <div class="app">
    <AppNotification class="container" />

    <NuxtLayout class="app__layout">
      <NuxtPage class="app__template" />
    </NuxtLayout>

    <ThirdpartyLoader :loadtime="7500" />

    <LazyDevtools />
  </div>
</template>

<script lang="ts" setup>
const { public: config } = useRuntimeConfig()
const { t } = useI18n({ useScope: 'global' })

const { fetchStoreData } = useStore()
fetchStoreData()

const { query } = useRoute()
const { locale } = useI18n()

useSeoMeta({
  // Set title tag
  title: t('meta title'),
  description: t('meta description'),
  // Set meta data
  formatDetection: 'telephone=no',
  msapplicationTileColor: '#16002b',
  themeColor: '#ffffff',
})

useHeadSafe({
  htmlAttrs: {
    lang: locale.value,
  },
  link: [
    { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
    { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32x32.png' },
    { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon-16x16.png' },
    { rel: 'manifest', href: '/site.webmanifest' },
    { rel: 'mask-icon', type: 'image/x-icon', href: '/safari-pinned-tab.svg', color: '#5bbad5' },
    { rel: 'preconnect', href: config.xcdnUrl },
  ],
})

/* Disable thirdparty scripts/widgets to test application performnce in for example Google Lighthouse */
if (query.thirdparty !== 'disabled' && config?.gtm?.id) {
  useHead({
    script: [{
      innerHTML: `window.dataLayer=(window.dataLayer||[]);window.dataLayer.push({'event':'gtm.js','gtm.start': new Date().getTime(),});`,
    }, {
      src: `${config?.gtm?.source || 'https://www.googletagmanager.com/gtm.js'}?id=${config.gtm.id}`,
      defer: config?.gtm?.defer || true,
    }],
  })
}

onMounted(() => {
  const route = useRoute()
  const path = route.path

  if (path === '/fr') {
    window.location.replace(`${window.location.origin}/fr/`)
  }
  else if (path === '/be') {
    window.location.replace(`${window.location.origin}/be/`)
  }
})
</script>

<style>
@import url('~layers/app/assets/css/vars.css');
@import url('~layers/app/assets/css/tokens/colors.css');
@import url('~layers/app/assets/css/base/reset.css');
@import url('~layers/app/assets/css/base/root-and-sectioning.css');
@import url('~layers/app/assets/css/base/forms-and-inputs.css');
@import url('~layers/app/assets/css/base/forms-and-inputs__file.css');
@import url('~layers/app/assets/css/base/forms-and-inputs__color.css');
@import url('~layers/app/assets/css/base/forms-and-inputs__range.css');
@import url('~layers/app/assets/css/base/forms-and-inputs__select.css');
@import url('~layers/app/assets/css/base/forms-and-inputs__checkbox-radio.css');
@import url('~layers/app/assets/css/base/text-content.css');
@import url('~layers/app/assets/css/base/text-content__headings.css');
@import url('~layers/app/assets/css/base/text-content__anchor-button.css');
@import url('~layers/app/assets/css/base/text-content__kbd.css');
@import url('~layers/app/assets/css/base/text-content__ruby.css');
@import url('~layers/app/assets/css/base/multimedia-and-embeds.css');
@import url('~layers/app/assets/css/base/lists.css');
@import url('~layers/app/assets/css/base/tables.css');
@import url('~layers/app/assets/css/base/details-summary.css');
@import url('~layers/app/assets/css/base/dialog-popover-backdrop.css');
@import url('~layers/app/assets/css/base/meter.css');
@import url('~layers/app/assets/css/base/progress.css');
@import url('~layers/app/assets/css/styleguide/_difference.css');
@import url('~layers/app/assets/css/utilities/utilities.css');
@import url('~layers/app/assets/css/utilities/buttons.css');
@import url('~layers/app/assets/css/utilities/messages.css');
@import url('~layers/app/assets/css/utilities/tables.css');
@import url('~layers/app/assets/css/vue-formkit.css');
@import url('~layers/app/app.css');
</style>
